import { prepareFormRequest } from './form';
import JustValidate from 'just-validate';
import {
  disableSuccessAndErrorMessage,
  formChecks,
  toggleClassError,
} from '../Validations/formChecks';
import {
  prepopulateCountrySelect,
  validateCheckboxGroup,
  validateRadioGroup,
  validateSelect,
  validateSelectCustom,
} from './select/select';
import { validateFileUploader } from '../fileUploader/fileUploader';
import { initContactUsRadioToggle } from '../container/container';

import * as validationRules from '../Validations/validationRules';
import {
  AccessibilityFormRequest,
  ContactRequest,
  getCountryCodeFromMetaTag,
  getQueryParam,
  NewsletterSubscriptions,
  NewsletterSubscriptionsConfirm,
  isAEMAuthorMode,
  scrollToComponent,
  StatusMatches,
} from '@msc-b2c/shared-utils';
import {
  ContactRequestRequest,
  NewsletterSubscriptionsConfirmRequest,
  NewsletterSubscriptionsRequest,
  StatusMatchesRequest,
} from '@msc-b2c/interfaces/interfaces';
import { clearSelectZipcode } from './inputPostcode/inputPostcode';
import { resetTextAreaCount } from '../textarea/textarea';
import { setCalendarToMonthYear } from './datePicker/datePicker';
import { Instance as FlatpickrInstance } from 'flatpickr/dist/types/instance';

export class FormContainer {
  private readonly formContainer: HTMLFormElement;
  private submitButton: HTMLButtonElement | null;
  public validator: JustValidate;
  private formAction: string;
  private autoTrigger: boolean;

  constructor(formContainer: HTMLFormElement) {
    this.formContainer = formContainer;
    this.submitButton = formContainer.querySelector('.cmp-button__submit');
    this.autoTrigger = Boolean(
      formContainer.querySelector('input[name="enableAutomaticActionTrigger"]')
    );
    // add instance for validation
    this.validator = new JustValidate(this.formContainer, {
      errorFieldCssClass: 'error-input',
      errorLabelCssClass: 'error-label',
      errorLabelStyle: {
        color: 'red',
        textDecoration: 'underline',
      },
      focusInvalidField: true,
      lockForm: true,
      validateBeforeSubmitting: true,
    });
    this.validator.onFail((fields) => {
      console.log('Validation Error - Errors: ', fields);
    });
    this.formAction = this.formContainer.dataset['action'] ?? '';
  }

  init() {
    this.bindEvents();
    this.formContainer.addEventListener('reset', () => {
      requestAnimationFrame(() => this.blankHourSelects());
    });
  }

  private submitForm() {
    this.submitButton?.addEventListener('click', (e) => {
      e.preventDefault();
      this.submitButton!.disabled = true;
      if (this.submitButton?.closest('.accessibility-form')) {
        this.handleAccessibilityFormSubmission();
      } else {
        this.handleFormSubmission();
      }
    });

    this.submitButton?.addEventListener(
      'keydown',
      (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.stopImmediatePropagation();
          this.submitButton?.click();
        }
      },
      true
    );
  }

  // add click event to form button
  bindEvents() {
    this.autoTrigger ? this.handleFormSubmission() : this.submitForm();
  }

  async handleAccessibilityFormSubmission() {
    const successMessage = this.formContainer?.querySelector(
      `.cmp-form__success-generic`
    ) as HTMLElement;
    const errorMessage = this.formContainer?.querySelector(`.cmp-form__error`) as HTMLElement;
    successMessage?.classList.add('hide');
    errorMessage?.classList.add('hide');

    if (this.checkValidation()) {
      const isValid = await this.validator.revalidate();
      if (isValid) {
        const formRequest = (await prepareFormRequest(this.formContainer)) as Record<string, any>;
        await this.performApiCall(
          new AccessibilityFormRequest(false, false, formRequest, ''),
          formRequest
        );
      } else {
        console.log('Validation Error - Form not submitted');
        const formRequest = await prepareFormRequest(this.formContainer);
        console.log('Form Request: ', formRequest);
        this.submitButton!.disabled = false;
      }
    } else {
      this.submitErrorsForScreenReader();
      const firstErrorOccured: HTMLElement | null = this.formContainer.querySelector(
        '.error-input, .error-select'
      );
      if (firstErrorOccured) {
        scrollToComponent(firstErrorOccured, true);
      }
      this.submitButton!.disabled = false;
    }
  }

  private blankHourSelects() {
    const root =
      this.formContainer.closest('.contactus-container') ||
      this.formContainer.parentElement ||
      document;

    root
      .querySelectorAll<HTMLSelectElement>("select[name='callback.selectedHour']")
      .forEach((sel) => {
        if (sel.form && sel.form !== this.formContainer) return;

        sel.selectedIndex = -1;
      });
  }

  async handleFormSubmission() {
    const successMessage = this.formContainer?.querySelector(
      `.cmp-form__success-generic`
    ) as HTMLElement;
    const errorMessage = this.formContainer?.querySelector(`.cmp-form__error`) as HTMLElement;
    successMessage?.classList.add('hide'); // If form submitted and press submit button again, hide success message
    errorMessage?.classList.add('hide'); // If form submitted and press submit button again, hide error message
    if (this.checkValidation()) {
      const isValid = await this.validator.revalidate();
      if (isValid) {
        let formRequest;
        switch (this.formAction) {
          case 'contact-requests':
            formRequest = (await prepareFormRequest(this.formContainer)) as ContactRequestRequest;
            await this.performApiCall(
              new ContactRequest(false, false, formRequest, ''),
              formRequest
            );
            break;
          case 'status-matches':
            formRequest = (await prepareFormRequest(this.formContainer)) as StatusMatchesRequest;
            await this.performApiCall(
              new StatusMatches(false, false, formRequest, ''),
              formRequest
            );
            break;
          case 'subscriptions':
            formRequest = (await prepareFormRequest(
              this.formContainer
            )) as NewsletterSubscriptionsRequest;
            await this.performApiCall(
              new NewsletterSubscriptions(false, false, formRequest, ''),
              formRequest
            );
            break;
          case 'subscriptions/confirm':
            formRequest = {
              token: encodeURIComponent(getQueryParam('token') ?? ''),
            } as NewsletterSubscriptionsConfirmRequest;
            await this.performApiCall(
              new NewsletterSubscriptionsConfirm(false, false, formRequest, ''),
              formRequest
            );
            break;
          case 'accessibility-form':
            formRequest = (await prepareFormRequest(this.formContainer)) as Record<string, any>;
            await this.performApiCall(
              new AccessibilityFormRequest(false, false, formRequest, ''),
              formRequest
            );
            break;
        }
      } else {
        console.log('Validation Error - Form not submitted');
        const formRequest = await prepareFormRequest(this.formContainer);
        console.log('Form Request: ', formRequest);
        this.submitButton!.disabled = false;
      }
    } else {
      this.submitErrorsForScreenReader();
      const firstErrorOccured: HTMLElement | null = this.formContainer.querySelector(
        '.error-input, .error-select'
      );
      if (firstErrorOccured) {
        scrollToComponent(firstErrorOccured, true);
      }
      this.submitButton!.disabled = false;
    }
  }

  performApiCall(
    apiToCall:
      | AccessibilityFormRequest
      | ContactRequest
      | StatusMatches
      | NewsletterSubscriptions
      | NewsletterSubscriptionsConfirm,
    formRequest:
      | Record<string, any>
      | ContactRequestRequest
      | StatusMatchesRequest
      | NewsletterSubscriptionsRequest
      | NewsletterSubscriptionsConfirmRequest
  ) {
    const successMessage = this.formContainer?.querySelector(
      `.cmp-form__success-generic`
    ) as HTMLElement;
    return apiToCall
      .sendData()
      .then((response) => {
        successMessage?.classList.remove('hide');
        successMessage.focus();
        successMessage?.scrollIntoView({ behavior: 'smooth', block: 'center' });

        setTimeout(() => successMessage?.classList.add('hide'), 3000);

        const analyticsSuccessEvent = new CustomEvent('formSuccess', {
          detail: {
            data: response,
            leadEmail: 'customer' in formRequest ? formRequest.customer?.email ?? '' : '',
          },
        });
        this.formContainer.dispatchEvent(analyticsSuccessEvent);
        this.setFieldsToDefault();
      })
      .catch((error) => {
        // get error status from fetch
        const apiError = error.issue_code;

        //select the error message to display based on the error code
        const errorHtmlEl: HTMLElement | null = this.formContainer.querySelector(
          `[data-error-code="${apiError}"]`
        );

        //seect a generic error message
        const genericErrorHtmlEl: HTMLElement | null = this.formContainer.querySelector(
          `[data-error-code="generic"]`
        );

        // if the error code is not found, display the generic error message
        if (errorHtmlEl) {
          errorHtmlEl.classList.remove('hide');
          scrollToComponent(errorHtmlEl, true);
          setTimeout(() => errorHtmlEl.classList.add('hide'), 3000);
        } else if (genericErrorHtmlEl) {
          genericErrorHtmlEl.classList.remove('hide');
          scrollToComponent(genericErrorHtmlEl, true);
          setTimeout(() => genericErrorHtmlEl.classList.add('hide'), 3000);
        }

        console.error('Request error:', error);
        const analyticsErrorEvent = new CustomEvent('formError', {
          detail: {
            leadError: error || 'Submission failed',
          },
        });
        this.formContainer.dispatchEvent(analyticsErrorEvent);
        this.formContainer.reset();
        this.setFieldsToDefault();
      })
      .finally(() => {
        this.submitButton!.disabled = false;
      });
  }

  checkValidation(): boolean {
    const inputs = Array.from(
      this.formContainer.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('input, textarea')
    );
    const selectsArray = Array.from(this.formContainer.querySelectorAll('select'));

    //initialize the form validation to true, then check each input and if it is not valid set it to false
    let isWholeFormValid = true;

    for (const input of inputs) {
      if (input.closest('.disabled-element-input-select')) {
        continue;
      }

      if (
        input.getAttribute('data-validation')?.includes('vr-mandatory-field') &&
        input.value.trim() === ''
      ) {
        validationRules.vrMandatoryField(input.value, 'vr-mandatory-field', input.id);
        if (input.getAttribute('data-validation')?.includes('vr-phone-number')) {
          toggleClassError(input.id, true, true);
          const parentElement = input.parentElement?.parentElement;
          parentElement
            ?.querySelector('.error-list-validation')
            ?.querySelector('.vr-mandatory-field')
            ?.classList.remove('input-hidden-error-messagge-vr');
        } else {
          toggleClassError(input.id, true);
        }
        isWholeFormValid = false;
      }
    }

    const customSelects: NodeListOf<HTMLElement> =
      this.formContainer.querySelectorAll('.custom-select');
    customSelects.forEach((element: HTMLElement) => {
      const isValid = validateSelectCustom(element);
      if (!isValid) {
        isWholeFormValid = false;
      }
    });

    let areSelectsValid = true;
    selectsArray.forEach((select: HTMLSelectElement) => {
      const isValid = validateSelect(select);
      if (!isValid) {
        areSelectsValid = false;
      }
    });

    if (!areSelectsValid) {
      isWholeFormValid = false;
    }

    const isRadioValid = this.formContainer.querySelector('input[type="radio"]')
      ? validateRadioGroup(this.formContainer)
      : true;
    if (!isRadioValid) {
      isWholeFormValid = false;
    }

    const isCheckboxValid = this.formContainer.querySelector('input[type="checkbox"]')
      ? validateCheckboxGroup(this.formContainer)
      : true;

    const fileUploaderInput: HTMLInputElement | null = this.formContainer.querySelector(
      '.cmp-file-uploader__input'
    );
    const isFileUploaderValid = fileUploaderInput ? validateFileUploader(fileUploaderInput) : true;

    if (!isCheckboxValid || !isFileUploaderValid) {
      isWholeFormValid = false;
    }

    return isWholeFormValid;
  }

  setFieldsToDefault() {
    // Reset: Name, Last Name, Mobile Number, Email, Company Name, Membership Card Number, Signature
    this.formContainer.reset();

    // Reset Date of birth
    const dateInput = this.formContainer.querySelector('.flatpickr-input') as HTMLInputElement & {
      _flatpickr?: FlatpickrInstance;
    };

    if (dateInput?._flatpickr) {
      dateInput._flatpickr.setDate('');
      setCalendarToMonthYear(dateInput._flatpickr, dateInput.getAttribute('data-date-default'));
    }

    // Reset Upload file
    if (this.formContainer.querySelector('.cmp-file-uploader__button')) {
      (this.formContainer.querySelector('.cmp-file-uploader__button') as HTMLButtonElement).click();
      (this.formContainer.querySelector('.cmp-file-uploader__input') as HTMLInputElement).value =
        '';
    }

    // Reset inputTel dial country
    const inputTelField = this.formContainer.querySelector(
      '.cmp-form-text__text[type="tel"]:not(.tel-input-not-editorial)'
    );
    if (inputTelField) {
      const inputTelInstance = (inputTelField as any)._itiInstance;
      if (inputTelInstance) inputTelInstance.setCountry(getCountryCodeFromMetaTag()); // reset tel to initial country
    }

    // Reset Country
    const coutrySelect = this.formContainer.querySelector(
      '[name="customer.address.countryCode"]'
    ) as HTMLInputElement;
    coutrySelect && prepopulateCountrySelect(coutrySelect, true);

    // Reset City
    if (this.formContainer.querySelector('[name="customer.address.town"]') as HTMLSelectElement)
      (
        this.formContainer.querySelector('[name="customer.address.town"]') as HTMLSelectElement
      ).value = '';

    // Reset Postcode
    this.formContainer.querySelector('.button-zip-code') && clearSelectZipcode(this.formContainer);

    // Reset Membership Status
    const membershipStatus = this.formContainer.querySelector(
      '[name="existingMembership.status"]'
    ) as HTMLSelectElement;

    const membershipOptions = this.formContainer.querySelectorAll(
      '[name="existingMembership.status"] option'
    ) as unknown as HTMLOptionElement[];

    membershipOptions?.forEach((o) => o.remove());
    if (membershipStatus) membershipStatus.value = '';

    this.formContainer
      .querySelector('[name="callback.asSoonAsPossible:boolean"]:checked')
      ?.dispatchEvent(new Event('change'));

    // Reset character count for textareas
    this.formContainer
      .querySelectorAll('textarea')
      .forEach((textarea) => resetTextAreaCount(textarea));
  }
  /**
   *
   * @var srDiv: A div added only for screen readers to be able to read all the errors
   * appearing when a submission fails because of validation rules in the fields of a form
   * @var srMessage: Screen reader message, the final div that has collected all error messages
   */
  submitErrorsForScreenReader() {
    const srDiv = this.formContainer.querySelector('.cmp-form_submission-errors');
    if (!srDiv) return;
    srDiv.innerHTML = '';
    let errorList = '';
    const inputsWithError = this.formContainer.querySelectorAll('.error-input');
    inputsWithError.forEach((input) => {
      const inputLabel = input.querySelector('label')?.textContent;
      const inputErrors = input.querySelector('.error-list-validation')?.querySelectorAll('div');
      let visibleError;
      inputErrors?.forEach((error) => {
        if (!error.classList.contains('input-hidden-error-messagge-vr')) {
          visibleError = error.textContent;
        }
      });
      if (inputLabel && visibleError) {
        errorList += `${inputLabel}: ${visibleError}. `;
      }
      const srMessage = document.createElement('div');
      srMessage.className = 'sr-only';
      srMessage.role = 'alert';
      srMessage.setAttribute('aria-live', 'assertive');

      setTimeout(() => {
        srMessage.textContent = errorList;
        srDiv.appendChild(srMessage);
      }, 0);
    });
  }
}

const observeFormInsertion = () => {
  const observer = new MutationObserver(() => {
    document.querySelectorAll('.cmp-form').forEach((node) => {
      // Avoid initializing the same form multiple times
      if (!node.hasAttribute('data-form-initialized')) {
        const form = new FormContainer(node as HTMLFormElement);
        form.init();

        node.querySelectorAll('input, textarea, select').forEach((input) => {
          input.addEventListener('blur', (e) => {
            formChecks(node as HTMLFormElement, form.validator, e);
          });
          input.addEventListener('focus', (e) => {
            const target = e.target as HTMLElement;
            if (target.classList.contains('cmp-form-text__text')) {
              if (window.innerWidth <= 768) {
                target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                disableSuccessAndErrorMessage(node as HTMLFormElement);
              }
            }
          });
        });
        node.querySelectorAll('select').forEach((input) => {
          input.addEventListener('change', (e) => {
            formChecks(node as HTMLFormElement, form.validator, e);
            disableSuccessAndErrorMessage(node as HTMLFormElement);
          });
        });

        node
          .querySelectorAll('.contactus-container')
          .forEach((container) => initContactUsRadioToggle(container));

        // Mark as initialized
        node.setAttribute('data-form-initialized', 'true');
      }
    });
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });
};

function observeFormErrors() {
  // Helper function to calculate the rendered width of the text content of an element
  function getTextWidth(el: Element) {
    // Create a temporary span
    const span = document.createElement('span');
    span.style.visibility = 'hidden';
    span.style.whiteSpace = 'nowrap';

    // Copy font styles from the element
    const style = window.getComputedStyle(el);
    span.style.font = style.font;
    span.style.fontSize = style.fontSize;
    span.style.fontWeight = style.fontWeight;
    span.style.fontFamily = style.fontFamily;
    span.style.letterSpacing = style.letterSpacing;
    span.style.textTransform = style.textTransform;

    // Set the span's text content to the element's text content
    span.textContent = el.textContent;

    document.body.appendChild(span);
    const width = span.getBoundingClientRect().width;
    document.body.removeChild(span);

    return width;
  }

  // Function to update the visibility class on error lists based on error text width
  function updateVisibilityClass() {
    errorLists.forEach((el) => {
      // Find the first error that is visible
      const visibleError = Array.from(el.children).find(
        (child) => window.getComputedStyle(child).display === 'block'
      );

      if (visibleError) {
        // If a visible error exists, find the associated input element
        const inputEl = el.parentElement?.querySelector('input');

        if (inputEl) {
          const inputElWidth = parseInt(window.getComputedStyle(inputEl).width);
          const errorTextWidth = getTextWidth(visibleError);

          // If the error text width exceeds the input width
          if (errorTextWidth > inputElWidth) {
            el.classList.add('has-visible-error');
          }
        }
      } else {
        // If no visible error, remove the CSS class if present
        el.classList.remove('has-visible-error');
      }
    });
  }

  // Helper debounce function to limit how often a function can be called
  function debounce<T extends Function>(fn: T, delay: number) {
    let timer: NodeJS.Timeout | undefined;

    return function (this: ThisParameterType<T>) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this), delay);
    };
  }

  const errorLists = document.querySelectorAll('.error-list-validation');
  const debouncedUpdate = debounce(updateVisibilityClass, 200);
  const observer = new MutationObserver(() => debouncedUpdate());

  // Start observing each error list element for child list and attribute changes
  errorLists.forEach((el) => {
    observer.observe(el, {
      attributes: true,
      attributeFilter: ['style', 'class'],
    });
  });

  setInterval(updateVisibilityClass, 1000);
}

const initFormsRuntime = () => {
  if (!isAEMAuthorMode()) {
    observeFormInsertion();
    observeFormErrors();
  }
};

if (document.readyState !== 'loading') {
  initFormsRuntime();
} else {
  document.addEventListener('DOMContentLoaded', initFormsRuntime);
}
